import type { LoadLevel, LoadStatus } from '@core/line-probe';
import { useTranslation } from 'react-i18next';
import { numberFormat } from './format';
import { fiveMinuteMegabytes, LOAD_LEVELS, loadKbps } from './loadLevels';

const LEVEL_KEYS: Record<LoadLevel, string> = {
    off: 'TestSuite.Line.Load.Off',
    low: 'TestSuite.Line.Load.Low',
    medium: 'TestSuite.Line.Load.Medium',
    high: 'TestSuite.Line.Load.High'
};

/** Spec 4.6 refusals the user can act on; any other reason is shown with its code. */
const UNAVAILABLE_KEYS: Record<string, string> = {
    load_budget: 'TestSuite.Line.LoadStatus.LoadBudget',
    not_allowed: 'TestSuite.Line.LoadStatus.NotAllowed',
    not_udp: 'TestSuite.Line.LoadStatus.NotUdp'
};

export function useLoadStatusText(status: LoadStatus): string | null {
    const { t } = useTranslation();
    switch (status.state) {
        case 'connecting':
            return t('TestSuite.Line.LoadStatus.Connecting');
        case 'running':
            return t('TestSuite.Line.LoadStatus.Running');
        case 'unavailable': {
            const key = status.reason ? UNAVAILABLE_KEYS[status.reason] : undefined;
            return key ? t(key) : t('TestSuite.Line.LoadStatus.Other', { reason: status.reason ?? '?' });
        }
        default:
            return null;
    }
}

/** Off, Low, Medium or High, each with its bitrate and what five minutes of it cost; switchable while the test runs. */
export const LoadChoice = ({ level, onChange, status }: { level: LoadLevel; onChange: (level: LoadLevel) => void; status: LoadStatus }) => {
    const { t, i18n } = useTranslation();
    const statusText = useLoadStatusText(status);

    return (
        <fieldset className="min-w-0">
            <legend className="font-semibold mb-1">{t('TestSuite.Line.Load.Legend')}</legend>
            <p className="text-sm mb-2">{t('TestSuite.Line.Load.Help')}</p>
            <div className="flex flex-col gap-1">
                {LOAD_LEVELS.map((l) => {
                    const kbps = l === 'off' ? 0 : loadKbps(l);
                    return (
                        <label key={l} className="inline-flex items-baseline gap-2 cursor-pointer">
                            <input type="radio" name="line-quality-load" value={l} checked={level === l} onChange={() => onChange(l)} />
                            <span>
                                {t(LEVEL_KEYS[l])}
                                {l !== 'off' && (
                                    <span className="text-sm opacity-70">
                                        {' – '}
                                        {t('TestSuite.Line.Load.Rate', {
                                            mbps: numberFormat(i18n.language, 1).format(kbps / 1000),
                                            mb: fiveMinuteMegabytes(kbps)
                                        })}
                                    </span>
                                )}
                            </span>
                        </label>
                    );
                })}
            </div>
            {statusText && (
                <p role="status" className={`text-sm mt-2 ${status.state === 'unavailable' ? 'text-warning' : ''}`}>
                    {statusText}
                </p>
            )}
        </fieldset>
    );
};

export const levelLabelKey = (level: LoadLevel | 'mixed') => (level === 'mixed' ? 'TestSuite.Line.Load.Mixed' : LEVEL_KEYS[level]);
